import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Truck, Sparkles, Heart } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import { Mandala, PaisleyDivider, Lotus, CornerOrnament, Paisley } from "@/lib/motifs";
import ProductCard from "@/components/ProductCard";

const TESTIMONIALS = [
    { name: "Priya Sharma", location: "Jaipur", text: "The meenakari kada set felt like an heirloom the moment I opened it. My grandmother approved — that's the highest honour." },
    { name: "Ananya Krishnan", location: "Chennai", text: "Kanganya's temple bangles were the centrepiece of my wedding. The craftsmanship on the Lakshmi engravings is unmatched." },
    { name: "Divya Roy", location: "Kolkata", text: "Finally an online store that treats shakha-pola with the reverence it deserves. Beautifully packaged and full of soul." },
];

const Home = () => {
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        fetchProducts({ featured: true }).then(setFeatured).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
                        <section className="hero-section relative">
                <div className="hero-bg-layer">
                {/* Static ivory + gold + maroon background (no video, so the page stays smooth) */}
                <div className="hero-bg-static" aria-hidden="true" />
                </div>

                <Mandala className="absolute -top-10 -left-16 opacity-15 slow-spin z-[2]" size={320} />
                <Mandala className="absolute top-1/2 -right-24 opacity-10 z-[2]" size={380} />
                <CornerOrnament className="absolute top-0 right-0 opacity-30 hidden lg:block z-[2]" size={160} flip="scaleX(-1)" />

                <div className="container-x pt-20 md:pt-28 pb-24 md:pb-32 relative z-10 w-full">
                    <div className="relative z-10 max-w-2xl">
                        <p className="font-devanagari text-[color:var(--gold)] text-sm md:text-base tracking-wide rise-in">
                            स्वागत है · A Kanganya Bangle Atelier
                        </p>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[color:var(--maroon)] mt-4 rise-in delay-100">
                            The soul of India,
                            <br />
                            <span className="italic text-[color:var(--copper)]">worn on the wrist.</span>
                        </h1>
                        <PaisleyDivider className="mt-6 max-w-md rise-in delay-200" />
                        <p className="font-body text-base md:text-lg text-[color:var(--ink-muted)] mt-6 max-w-lg leading-relaxed rise-in delay-300">
                            Kanganya is a Manihar bangle house — carrying forward the community that has shaped, coloured and blessed the wrists of Indian women for generations. Glass, lac, kundan, silver, meenakari — each chudi handmade, each stack a small ceremony.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8 rise-in delay-400">
                            <Link to="/shop" className="btn-primary" data-testid="hero-shop-cta">
                                Explore Collection <ArrowRight size={14} />
                            </Link>
                            <Link to="/story" className="btn-secondary" data-testid="hero-story-cta">
                                Our Heritage
                            </Link>
                        </div>

                        <div className="flex flex-wrap gap-6 mt-10 pt-8 border-t border-[color:var(--border-gold)] rise-in delay-400">
                            {[
                                { icon: Heart, label: "Manihar Handcrafted" },
                                { icon: Truck, label: "Free Pan-India Shipping" },
                                { icon: Sparkles, label: "Traditional & Designer Bangles" },
                            ].map((f) => (
                                <div key={f.label} className="flex items-center gap-2 text-xs font-ui tracking-widest-x uppercase text-[color:var(--ink-muted)]">
                                    <f.icon size={14} className="text-[color:var(--copper)]" strokeWidth={1.5} />
                                    {f.label}
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </section>



            {/* FEATURED COLLECTION */}
            <section className="relative py-20">
                <div className="container-x">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <p className="font-devanagari text-[color:var(--gold)] text-sm">विशेष संग्रह</p>
                            <h2 className="font-heading text-4xl md:text-5xl text-[color:var(--maroon)] mt-1">The Curated Edit</h2>
                            <p className="font-body text-[color:var(--ink-muted)] mt-2 max-w-lg">Chosen this fortnight by our master craftsmen — a rotating selection of our most coveted heritage pieces.</p>
                        </div>
                        <Link to="/shop" className="font-ui text-xs tracking-widest-x uppercase text-[color:var(--maroon)] hover:text-[color:var(--copper)] flex items-center gap-2 transition-colors duration-300" data-testid="view-all-products">
                            View All <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-testid="featured-grid">
                        {featured.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="relative py-20 overflow-hidden" style={{ backgroundColor: "var(--maroon)" }}>
                <Mandala className="absolute top-1/2 -left-24 opacity-10 -translate-y-1/2" size={340} color="#C9A227" />
                <Mandala className="absolute -bottom-24 -right-24 opacity-10" size={320} color="#C9A227" />
                <div className="container-x relative">
                    <div className="text-center mb-12">
                        <Lotus className="mx-auto mb-3" color="#C9A227" />
                        <p className="font-devanagari text-[color:var(--gold)] text-sm">प्रशंसापत्र</p>
                        <h2 className="font-heading text-4xl md:text-5xl text-[color:var(--ivory)] mt-1">Words from our women</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={t.name}
                                className="relative border border-[color:var(--gold)]/40 p-8 bg-[color:var(--maroon-dark)]/40 rise-in"
                                style={{ animationDelay: `${i * 100}ms` }}
                                data-testid={`testimonial-${i}`}
                            >
                                <Paisley className="absolute -top-3 left-6 bg-[color:var(--maroon)] px-1" color="#C9A227" size={20} />
                                <div className="flex gap-1 mb-4">
                                    {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={13} fill="#C9A227" stroke="#C9A227" />)}
                                </div>
                                <p className="font-body italic text-[color:var(--ivory)]/90 leading-relaxed">"{t.text}"</p>
                                <div className="mt-6 pt-4 border-t border-[color:var(--gold)]/25">
                                    <p className="font-heading text-lg text-[color:var(--gold)]">{t.name}</p>
                                    <p className="font-ui text-[10px] tracking-widest-x uppercase text-[color:var(--ivory)]/60 mt-0.5">{t.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;